/**
 * Routes that exist only in the Angular router: no Strapi entry, no old-CMS page, so a CMS lookup for them
 * can only ever 404. Shared by the two places that would otherwise each make that pointless call:
 *
 * - `src/ssr/website.endpoint.ts` — skips the Strapi slug check and the CMS resolve, keeps the HTTP status at
 *   200, marks the response noindex and never stores the render in the SSR cache.
 * - `PageLoadService.proceedPageRequest` — skips the browser-side `page/resolve` call on navigation.
 *
 * `evaluate/` is a prefix rather than a slug because its second segment is a per-user check-in token: resolving
 * it would put that token in a request URL (and in one cache key per token) for a page that has no CMS content.
 */
export const APP_ONLY_SLUGS: ReadonlySet<string> = new Set(['success', 'cancel']);

export const APP_ONLY_PREFIXES: readonly string[] = ['evaluate/'];

/**
 * `path` may arrive as a raw router URL: with or without a leading slash, with a query string or hash, with the
 * wrapped-app `android_asset/www/` prefix, and with secondary-outlet segments (`/evaluate/abc(dialog:x)`). All are
 * normalised away before matching, mirroring `PageLoadService.getPageName`, so this check never disagrees with the
 * page name that would otherwise be resolved.
 */
export function isAppOnlyPath(path: string): boolean {
  const slug = path
    .split(/[?#]/)[0]
    .replace(/^\/+/, '')
    .replace(/^android_asset\/www\//, '')
    .split('(')[0];
  return APP_ONLY_SLUGS.has(slug) || APP_ONLY_PREFIXES.some(prefix => slug.startsWith(prefix));
}
